import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Badge, Box, Button, Drawer, Group, ScrollArea, Text } from '@mantine/core';
import { IconShoppingCart } from '@tabler/icons-react';
import { CartStore } from './CartStore';
import { CartController } from './CartController';
import { CartItem } from './CartItem';
import { CheckoutButton } from './CheckoutButton';
import { THEME_COLORS } from '../../theme/ThemeConstants';

interface MobileCartViewProps {
  cartStore: CartStore;
  controller: CartController;
  animatingItems: Map<string, 'add' | 'remove'>;
  animationDuration: { ADD: number; REMOVE: number };
  onRemoveItem: (lineId: string) => void;
}

export const MobileCartView: React.FC<MobileCartViewProps> = observer(({
  cartStore,
  controller,
  animatingItems,
  animationDuration,
  onRemoveItem
}) => {
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);

  return (
    <>
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          width: '100%'
        }}
      >
        {!cartStore.isEmpty && (
          <Button
            size='md'
            variant='outline'
            onClick={() => setCartDrawerOpen(true)}
            styles={{
              root: {
                borderColor: THEME_COLORS.border.secondary,
                color: THEME_COLORS.text.primary,
                height: 48,
                '&:hover': {
                  backgroundColor: THEME_COLORS.background.secondary
                }
              },
              label: {
                color: THEME_COLORS.text.primary
              }
            }}
          >
            <Group gap={6}>
              <IconShoppingCart size={18} style={{ color: THEME_COLORS.text.primary }} />
              <Text size='sm' fw={600} c={THEME_COLORS.text.primary}>
                Cart
              </Text>
              <Badge
                size='sm'
                circle
                style={{
                  backgroundColor: THEME_COLORS.accent.primary,
                  color: 'white',
                  border: 'none'
                }}
              >
                {cartStore.items.length}
              </Badge>
            </Group>
          </Button>
        )}

        <CheckoutButton
          isEmpty={cartStore.isEmpty}
          isLoading={cartStore.isLoading}
          total={cartStore.total}
          currency={cartStore.currency}
          itemCount={cartStore.items.length}
          onClick={() => controller.handleCheckout()}
        />
      </Box>

      <Drawer
        opened={cartDrawerOpen}
        onClose={() => setCartDrawerOpen(false)}
        position='bottom'
        size='75%'
        title={
          <Text size='lg' fw={700} c={THEME_COLORS.text.primary}>
            Cart Items ({cartStore.items.length})
          </Text>
        }
        styles={{
          header: {
            backgroundColor: THEME_COLORS.background.tertiary,
            borderBottom: `1px solid ${THEME_COLORS.border.secondary}`
          },
          body: {
            backgroundColor: THEME_COLORS.background.tertiary,
            padding: 16
          },
          close: {
            color: THEME_COLORS.text.primary
          }
        }}
      >
        <ScrollArea style={{ height: '100%' }}>
          <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {cartStore.items.map((item) => {
              const isItemLoading = cartStore.isLoading && cartStore.loadingLineId === item.id;
              const isUpdateLoading = isItemLoading && cartStore.loadingOperation === 'update';
              const animationType = animatingItems.get(item.id);

              return (
                <CartItem
                  key={item.id}
                  item={item}
                  isLoading={cartStore.isLoading}
                  isUpdateLoading={isUpdateLoading}
                  isRemoving={animationType === 'remove'}
                  animationType={animationType}
                  animationDuration={animationDuration[animationType === 'add' ? 'ADD' : 'REMOVE']}
                  onUpdateQuantity={(lineId, quantity) => controller.updateQuantity(lineId, quantity)}
                  onRemove={onRemoveItem}
                />
              );
            })}
          </Box>
        </ScrollArea>
      </Drawer>
    </>
  );
});
